import type { RemoveFromDriverResponse } from '../../../services/orders.service';

/** One applied move as `/applied-plan-moves` hands it to undo. */
export interface UndoableMove {
  assignment_id: number;
  pronumber: number;
  legnum: number;
  driver: string;
  /** Who held the leg before the apply: '' for nobody, null when the row was applied
   * before that was recorded. */
  original_driver?: string | null;
  /** Set when the apply performed a street turn: the import whose container went onto
   * `pronumber`, the export. The move is the export's originate leg. */
  street_turn_import?: number | null;
}

/** What undo does to a move (planner feedback, 2026-10-28):
 *  - clear: take the plan's driver off, as undo always has.
 *  - restore: the apply took the leg from another driver, so it goes back to them.
 *  - keep: the leg was already this driver's before the apply, so it stays.
 *  - turn: take the driver off the export's leg, then reverse the reuse.
 *  - turn_not_permitted: the user may not undo container reuses, so the turn is left
 *    as it is, driver and all, the same as the order screen hides its Undo Reuse. */
export type UndoStep = 'clear' | 'restore' | 'keep' | 'turn' | 'turn_not_permitted';

export function undoStep(move: UndoableMove, mayUndoTurns: boolean): UndoStep {
  if (move.street_turn_import) return mayUndoTurns ? 'turn' : 'turn_not_permitted';
  const before = (move.original_driver ?? '').trim();
  if (!before) return 'clear';
  return before === move.driver.trim() ? 'keep' : 'restore';
}

export function planUndo<M extends UndoableMove>(moves: M[], mayUndoTurns: boolean) {
  const steps: Record<UndoStep, M[]> = { clear: [], restore: [], keep: [], turn: [], turn_not_permitted: [] };
  for (const move of moves) steps[undoStep(move, mayUndoTurns)].push(move);
  return steps;
}

/** The moves whose driver actually came off. A response that names its failures is
 * believed; one that only counts them is believed only when nothing failed, because a
 * turn's reuse must not be reversed under a driver still on its leg. */
export function driverRemoved<M extends UndoableMove>(moves: M[], response: RemoveFromDriverResponse): M[] {
  if (response.success === 0) return [];
  if (response.failedItems) {
    const failed = new Set(response.failedItems.map(f => `${f.proNumber}-${f.legnum}`));
    return moves.filter(m => !failed.has(`${m.pronumber}-${m.legnum}`));
  }
  return response.failed > 0 ? [] : moves;
}

/** The reason an undo-reuse was refused, as the order screen's Undo Reuse words it. */
export function reuseError(err: unknown): string {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = e?.response?.data?.detail as { errors?: unknown } | string | undefined;
  if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) return detail.errors.join('; ');
  if (typeof detail === 'string') return detail;
  return e?.message || 'Undo reuse failed';
}
